<template>
    <div class="gst-navigation-drawer-menu">
        <v-list
            v-if="isMinimized"
            open-strategy="single">
            <v-list-item
                to="/"
                :data-test-id="$testId('menuItem.dashboard')">
                <template #prepend>
                    <div>
                        <v-icon icon="mdi-view-dashboard-outline" />
                    </div>
                </template>
            </v-list-item>

            <v-list-item
                v-if="activeProductManager"
                to="/theHotelPriceRules">
                <template #prepend>
                    <IconPriceRules class="mr-0" />
                </template>
            </v-list-item>

            <v-list-item
                v-if="activeCustomerSupport"
                to="/customerSupport/search">
                <template #prepend>
                    <div>
                        <v-icon icon="mdi-account-search" />
                    </div>
                </template>
            </v-list-item>

            <v-list-item
                v-if="
                    activeCustomerReports ||
                    activeSalesReports ||
                    activeFinanceReports ||
                    activeProductViewer ||
                    activeGstCustomerSupport
                "
                @click="onClickMenuItemDo">
                <template #prepend>
                    <div>
                        <IconProductsReports class="mr-0" />
                    </div>
                </template>
            </v-list-item>

            <v-list-item
                v-if="activeProductManager || activeProductViewer"
                @click="onClickMenuItemDo">
                <template #prepend>
                    <IconMarketing class="mr-0" />
                </template>
            </v-list-item>
        </v-list>

        <v-list
            v-else
            open-strategy="single">
            <v-list-item
                to="/"
                :data-test-id="$testId('menuItem.dashboard')"
                :title="$t('navigationTree.dashboard.children.dashboard')">
                <template #prepend>
                    <div>
                        <v-icon icon="mdi-view-dashboard-outline" />
                    </div>
                </template>
            </v-list-item>

            <h5
                v-if="activeProductManager"
                class="mt-5 ml-4 text-overline">
                {{ $t('navigationTree.hotelManagement.group') }}
            </h5>
            <v-list-item
                v-if="activeProductManager"
                :data-test-id="$testId('menuItem.priceRules')"
                :title="$t('navigationTree.hotelManagement.children.priceRules')"
                to="/theHotelPriceRules"
                @click="onClickMenuItemDo">
                <template #prepend>
                    <IconPriceRules class="mr-0" />
                </template>
            </v-list-item>

            <h5
                v-if="activeCustomerSupport"
                class="mt-5 ml-4 text-overline">
                {{ $t('navigationTree.customerSupport.group') }}
            </h5>
            <v-list-item
                v-if="activeCustomerSupport"
                :data-test-id="$testId('menuItem.customerSearch')"
                to="/customerSupport/search"
                :title="$t('navigationTree.customerSupport.children.customerSearch')"
                @click="onClickMenuItemDo">
                <template #prepend>
                    <div>
                        <v-icon icon="mdi-account-search" />
                    </div>
                </template>
            </v-list-item>

            <h5
                v-if="activeCustomerReports || activeSalesReports || activeGstCustomerSupport"
                class="mt-5 ml-4 text-overline">
                {{ $t('navigationTree.reports.group') }}
            </h5>

            <template v-for="item in computedNestedMenu">
                <v-list-group
                    v-if="item.hasAccess"
                    :key="item.title"
                    :value="item.active"
                    :class="{
                        'gst-route--active': isMatchingRouteName(item.regexRouteName, $route.name),
                    }"
                    @click="onClickMenuItemDo">
                    <template #activator="{ props }">
                        <v-list-item
                            v-bind="props"
                            :title="item.title">
                            <template #prepend>
                                <component
                                    v-bind="props"
                                    :is="item.action" />
                            </template>
                        </v-list-item>
                    </template>
                    <v-list-item
                        v-for="(child, key) in item.items"
                        :key="key"
                        :to="child.to"
                        :title="child.title"
                        :subtitle="child.subtitle" />
                </v-list-group>
            </template>

            <h5
                v-if="activeProductManager || activeProductViewer"
                class="mt-5 ml-4 text-overline">
                {{ $t('navigationTree.marketing.group') }}
            </h5>
            <v-list-item
                v-if="activeProductManager || activeProductViewer"
                :data-test-id="$testId('menuItem.campaigns')"
                :title="$t('navigationTree.marketing.children.campaigns.parent')"
                to="/campaigns"
                @click="onClickMenuItemDo">
                <template #prepend>
                    <IconCampaigns class="mr-0" />
                </template>
            </v-list-item>
            <v-list-item
                v-if="activeProductManager || activeProductViewer"
                :data-test-id="$testId('menuItem.offerTokens')"
                :title="$t('navigationTree.marketing.children.offerTokens.parent')"
                to="/offerTokens"
                @click="onClickMenuItemDo">
                <template #prepend>
                    <IconOfferTokens class="mr-0" />
                </template>
            </v-list-item>
        </v-list>
    </div>
</template>

<!-- eslint-disable vue/no-unused-components -->
<script>
import { defineAsyncComponent } from 'vue';
import { rolesConstants } from '@utils/constants';

export default {
    name: 'NavigationDrawerMenu',
    i18nOptions: {
        namespaces: '_common',
        keyPrefix: '',
    },
    testIdOptions: {
        keyPrefix: 'navigationDrawer.menu',
    },
    components: {
        IconCustomerReports: defineAsyncComponent(
            () => import('@/assets/icons/IconCustomerReports')
        ),
        IconCustomerSupportReports: defineAsyncComponent(
            () => import('@/assets/icons/IconCustomerSupportReports')
        ),
        IconOrderReports: defineAsyncComponent(() => import('@/assets/icons/IconOrderReports')),
        IconProductsReports: defineAsyncComponent(
            () => import('@/assets/icons/IconProductsReports')
        ),
        IconFinanceReports: defineAsyncComponent(() => import('@/assets/icons/IconFinanceReports')),
        IconPriceRules: defineAsyncComponent(() => import('@/assets/icons/IconPriceRules')),
        IconCampaigns: defineAsyncComponent(() => import('@/assets/icons/IconCampaigns')),
        IconOfferTokens: defineAsyncComponent(() => import('@/assets/icons/IconOfferTokens')),
        IconMarketing: defineAsyncComponent(() => import('@/assets/icons/IconMarketing')),
    },
    emits: ['menu-item-clicked'],
    props: {
        isMinimized: {
            type: Boolean,
            default: false,
        },
        userAccessControlsConfig: {
            type: Object,
            default: null,
        },
    },
    computed: {
        computedNestedMenu() {
            return (
                this.userAccessControlsConfig && [
                    {
                        action: 'IconCustomerReports',
                        items: [
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.customers.children.activitiesSummary'
                                ),
                                to: '/reports/customers/activities',
                            },
                        ],
                        title: this.$t('navigationTree.reports.children.customers.parent'),
                        regexRouteName: new RegExp('^reports.customers'),
                        hasAccess: this.activeCustomerReports,
                    },
                    {
                        action: 'IconCustomerSupportReports',
                        items: [
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.customerSupport.children.bmtCancellations.title'
                                ),
                                to: '/reports/customerSupport/bmtCancellations',
                            },
                        ],
                        title: this.$t('navigationTree.reports.children.customerSupport.parent'),
                        regexRouteName: new RegExp('^reports.customerSupport'),
                        hasAccess: this.activeGstCustomerSupport,
                    },
                    {
                        action: 'IconFinanceReports',
                        items: [
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.finance.children.ordersSummary'
                                ),
                                to: '/reports/finance/summary',
                            },
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.finance.children.hotelReconciliation.title'
                                ),
                                subtitle: this.$t(
                                    'navigationTree.reports.children.finance.children.hotelReconciliation.subtitle'
                                ),
                                to: '/reports/finance/hotelReconciliation',
                            },
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.finance.children.hotelReconciliationHotelOnly.title'
                                ),
                                subtitle: this.$t(
                                    'navigationTree.reports.children.finance.children.hotelReconciliationHotelOnly.subtitle'
                                ),
                                to: '/reports/finance/hotelReconciliationHotelOnly',
                            },
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.finance.children.tmReconciliationDynamic.title'
                                ),
                                subtitle: this.$t(
                                    'navigationTree.reports.children.finance.children.tmReconciliationDynamic.subtitle'
                                ),
                                to: '/reports/finance/tmReconciliationDynamic',
                            },
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.finance.children.commissionSummary'
                                ),
                                to: '/reports/finance/commissionSummary',
                            },
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.finance.children.commissionDetails'
                                ),
                                to: '/reports/finance/commissionDetails',
                            },
                        ],
                        title: this.$t('navigationTree.reports.children.finance.parent'),
                        regexRouteName: new RegExp('^reports.finance'),
                        hasAccess: this.activeFinanceReports,
                    },
                    {
                        action: 'IconOrderReports',
                        items: [
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.orders.children.ordersSummary'
                                ),
                                to: '/reports/orders/summary',
                            },
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.orders.children.eventOrderDetails'
                                ),
                                to: '/reports/orders/event/orderDetails',
                            },
                        ],
                        title: this.$t('navigationTree.reports.children.orders.parent'),
                        regexRouteName: new RegExp('^reports.orders'),
                        hasAccess: this.activeSalesReports,
                    },
                    {
                        action: 'IconProductsReports',
                        items: [
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.products.children.topSellingEvents'
                                ),
                                to: '/reports/products/events',
                            },
                            {
                                title: this.$t(
                                    'navigationTree.reports.children.products.children.hotelDetails'
                                ),
                                to: '/reports/products/hotel-details',
                            },
                        ],
                        title: this.$t('navigationTree.reports.children.products.parent'),
                        regexRouteName: new RegExp('^reports.products'),
                        hasAccess: this.activeProductViewer,
                    },
                ]
            );
        },
        activeCustomerSupport() {
            return (
                this.userAccessControlsConfig &&
                this.userAccessControlsConfig.roles[rolesConstants.OIDC_ROLES.CUSTOMER_VIEWER]
                    .active
            );
        },
        activeCustomerReports() {
            return (
                this.userAccessControlsConfig &&
                this.userAccessControlsConfig.roles[rolesConstants.OIDC_ROLES.CUSTOMER_REPORTS]
                    .active
            );
        },
        activeGstCustomerSupport() {
            return (
                this.userAccessControlsConfig &&
                this.userAccessControlsConfig.roles[rolesConstants.OIDC_ROLES.GST_CUSTOMER_SUPPORT]
                    .active
            );
        },
        activeSalesReports() {
            return (
                this.userAccessControlsConfig &&
                this.userAccessControlsConfig.roles[rolesConstants.OIDC_ROLES.SALES_REPORTS].active
            );
        },
        activeProductViewer() {
            return (
                this.userAccessControlsConfig &&
                this.userAccessControlsConfig.roles[rolesConstants.OIDC_ROLES.PRODUCT_VIEWER].active
            );
        },
        activeFinanceReports() {
            return (
                this.userAccessControlsConfig &&
                this.userAccessControlsConfig.roles[rolesConstants.OIDC_ROLES.FINANCE_REPORTS]
                    .active
            );
        },
        activeProductManager() {
            return (
                this.userAccessControlsConfig &&
                this.userAccessControlsConfig.roles[rolesConstants.OIDC_ROLES.PRODUCT_MANAGER]
                    .active
            );
        },
    },
    methods: {
        isMatchingRouteName(regex, text) {
            return regex.test(text);
        },
        onClickMenuItemDo() {
            this.$emit('menu-item-clicked');
        },
    },
};
</script>
<style lang="scss">
@import '@scssVariables';

.gst-navigation-drawer-menu {
    .v-list {
        .v-list-item--active:hover,
        .v-list-item--active {
            color: theme-color('primary');

            .v-list-item__prepend svg path[stroke] {
                stroke: theme-color('primary');
            }

            .v-list-item__prepend svg path[fill] {
                fill: theme-color('primary');
            }
        }

        .v-list-item--active::before {
            opacity: 0;
        }

        .v-list-item--active:hover::before {
            opacity: 0.1;
        }

        .v-list-item-title {
            font-size: font-size('s');
            font-weight: font-weight('medium');
        }

        .v-list-item__prepend {
            margin-right: 10px;
        }
    }

    .v-list {
        .v-list-item:not(.v-list-item--active):not(.v-list-item--disabled) {
            color: theme-color('quaternary') !important;
        }

        .v-list-group.gst-route--active {
            & > .v-list-item {
                color: theme-color('primary') !important;
                .gst-svg-icon.gst-svg-icon--stroke path,
                .gst-svg-icon.gst-svg-icon--stroke {
                    stroke: theme-color('primary');
                }

                .gst-svg-icon.gst-svg-icon--fill {
                    fill: theme-color('primary');
                }

                .v-icon {
                    color: theme-color('primary') !important;
                }

                .v-list-item__prepend svg path {
                    stroke: theme-color('primary');
                }
            }
        }

        .v-list-item--link:before,
        .v-list-item:before {
            background-color: theme-color('secondary');
        }

        .v-list-item,
        .v-list-item__append {
            min-height: 48px;
            font-weight: font-weight('medium');
            border-radius: 0 20px 20px 0;

            &:hover {
                color: theme-color('primary');
            }

            &:hover:before {
                opacity: 0.1;
            }
        }
    }
}
</style>
